import { CheckCircle, ClipboardCheck, AlertCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  RequirementCheck, 
  ManualVerificationState 
} from '@/hooks/useDocumentRequirements';

interface ManualRequirementsChecklistProps {
  checks: RequirementCheck[];
  manualState: ManualVerificationState;
  onManualStateChange: (state: ManualVerificationState) => void;
  disabled?: boolean;
}

export function ManualRequirementsChecklist({
  checks,
  manualState,
  onManualStateChange,
  disabled,
}: ManualRequirementsChecklistProps) {
  // Group checks into categories
  const autoVerified = checks.filter(c => c.canAutoVerify);
  const needsManualReview = checks.filter(c => c.requiresManualReview);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'pending_manual':
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'text-success';
      case 'fail':
        return 'text-destructive';
      case 'warning':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      {/* Auto-Verified Section */}
      {autoVerified.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              Automatically Verified ({autoVerified.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {autoVerified.map(check => (
                <div key={check.id} className="flex items-center gap-3 py-2">
                  {getStatusIcon(check.status)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{check.label}</p>
                    {check.message && (
                      <p className={`text-xs ${getStatusColor(check.status)}`}>
                        {check.message}
                      </p>
                    )}
                  </div>
                  {check.required && check.actual && (
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={check.status === 'pass' ? 'default' : check.status === 'fail' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {check.actual}
                      </Badge>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Review Section */}
      {needsManualReview.length > 0 && (
        <Card className="border-warning">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-warning" />
              Manual Review Required ({needsManualReview.length})
            </CardTitle>
            <CardDescription className="text-xs">
              These requirements could not be automatically verified. Please review the document and confirm each requirement is met.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {needsManualReview.map(check => {
                const isVerified = manualState[check.id]?.verified ?? false;
                const note = manualState[check.id]?.note ?? '';

                return (
                  <div 
                    key={check.id} 
                    className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                  >
                    <Checkbox
                      id={`manual-${check.id}`}
                      checked={isVerified}
                      onCheckedChange={(checked) => {
                        onManualStateChange({
                          ...manualState,
                          [check.id]: { verified: !!checked, note }
                        });
                      }}
                      disabled={disabled}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <Label
                        htmlFor={`manual-${check.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {check.label}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {check.description}
                      </p>
                      {check.required && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Required: <span className="font-medium">{check.required}</span>
                        </p>
                      )}

                      {/* Optional note field when verified */}
                      {isVerified && (
                        <Textarea
                          placeholder="Add verification note (optional)..."
                          value={note}
                          onChange={(e) => {
                            onManualStateChange({
                              ...manualState,
                              [check.id]: { verified: true, note: e.target.value }
                            });
                          }}
                          className="mt-2 h-16 text-xs"
                          disabled={disabled}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
